import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import AppDialog from "components/common/AppDialog";
import {
  filesStorageApi,
  useRenameBrowserFileMutation,
} from "features/fileSystem/filesStorage";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import type {
  BrowserRenameFileResponse,
  FileItem,
} from "types/interfaces/filesStorage";
import {
  renameProblem,
  renamedFileName,
  splitFileName,
} from "utils/fileRename";

interface FileRenameDialogProps {
  /** The file to rename; the dialog is open while it is set. */
  file: FileItem | null;
  onClose: () => void;
  onRenamed?: (result: BrowserRenameFileResponse) => void;
  /** Staff see what the rename means for the file's dates and for the bots. */
  staff?: boolean;
  /** A bot job reads the folder the file is in (from the listing). */
  botsReadFolder?: boolean;
  zIndex?: number;
}

const STILL_FINISHING = [504, "FETCH_ERROR", "TIMEOUT_ERROR"];

export default function FileRenameDialog({
  file,
  onClose,
  onRenamed,
  staff = false,
  botsReadFolder = false,
  zIndex,
}: FileRenameDialogProps) {
  const dispatch = useAppDispatch();
  const [renameFile] = useRenameBrowserFileMutation();
  const [base, setBase] = useState("");
  const [extension, setExtension] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A second Enter or click can come before the disabled button is drawn.
  const busy = useRef(false);

  useEffect(() => {
    if (!file) return;
    const parts = splitFileName(file.fname);
    setBase(parts.base);
    setExtension(parts.extension);
    setServerError(null);
  }, [file]);

  const newName = renamedFileName(base, extension);
  const problem = renameProblem(base, extension);
  const unchanged = !!file && newName === file.fname;
  const error = serverError ?? (unchanged ? null : problem);

  const close = () => {
    if (!submitting) onClose();
  };

  const notify = (
    message: string,
    severity: "success" | "error" | "info" | "warning",
  ) => dispatch(pushNotification({ message, severity }));

  const submit = async () => {
    if (!file || busy.current) return;
    if (unchanged) {
      onClose();
      return;
    }
    if (problem) return;
    busy.current = true;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await renameFile({
        source: file.fsource,
        new_name: newName,
      }).unwrap();
      notify(`Renamed to ${result.item.fname}`, "success");
      (result.warnings ?? []).forEach((warning) => notify(warning, "warning"));
      onRenamed?.(result);
      onClose();
    } catch (failure: unknown) {
      const status = (failure as { status?: number | string })?.status;
      if (status != null && STILL_FINISHING.includes(status)) {
        // The server goes on after the gateway gives up on the request.
        notify(
          "The rename is still finishing in storage; the list refreshes in a few seconds.",
          "info",
        );
        window.setTimeout(
          () => dispatch(filesStorageApi.util.invalidateTags(["FileSystem"])),
          5000,
        );
        onClose();
      } else {
        setServerError(
          (failure as { data?: { error?: string } })?.data?.error ||
            "Failed to rename the file.",
        );
      }
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={!!file}
      onClose={close}
      title="Rename file"
      maxWidth="xs"
      zIndex={zIndex}
      actions={
        <>
          <Button onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            variant="contained"
            disabled={submitting || (!!problem && !unchanged)}
          >
            {submitting ? "Renaming..." : "Rename"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          label="File name"
          value={base}
          disabled={submitting}
          error={!!error}
          helperText={error ?? " "}
          inputProps={{ "aria-label": "File name" }}
          onChange={(event) => {
            setBase(event.target.value);
            setServerError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void submit();
            }
          }}
        />
        {extension ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {extension}
          </Typography>
        ) : null}
      </Box>
      {staff ? (
        <Typography variant="body2" color="text.secondary">
          The file keeps its type. Storage keeps the old name in the file's
          version history, and the file shows the rename date as its last
          modified date.
          {botsReadFolder
            ? " Bot jobs read this folder: if an upload job already uploaded" +
              " this file, a run that processes this employee again uploads it" +
              " once more under the new name."
            : ""}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          The file keeps its type; only its name changes.
        </Typography>
      )}
    </AppDialog>
  );
}
